/**
 * Client View Context
 * 
 * Manages admin viewing mode for client portals.
 * When active, overrides the client context in the portal.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getStoredClientViewSession, 
  clearClientViewState,
  type ClientViewSession 
} from '../../services/impersonationService';

interface ClientViewContextType {
  /** Active client view session, null if not viewing as admin */
  session: ClientViewSession | null;
  /** Whether admin is currently viewing a client portal */
  isViewingAsAdmin: boolean;
  /** The client ID to use for queries (overrides user's own client) */
  effectiveClientId: string | null;
  /** The client name being viewed */
  viewedClientName: string | null;
  /** End the client view session and return to admin */
  exitClientView: () => void;
  /** Duration of current session in seconds */
  sessionDuration: number;
}

const ClientViewContext = createContext<ClientViewContextType | null>(null);

interface ClientViewProviderProps {
  children: ReactNode;
}

export const ClientViewProvider: React.FC<ClientViewProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [session, setSession] = useState<ClientViewSession | null>(null);
  const [sessionDuration, setSessionDuration] = useState(0);

  // Check for stored session on mount
  useEffect(() => {
    const stored = getStoredClientViewSession();
    if (stored) {
      // Validate session hasn't expired (4 hour timeout)
      const sessionStart = new Date(stored.started_at).getTime();
      const now = Date.now();
      const fourHours = 4 * 60 * 60 * 1000;
      
      if (now - sessionStart > fourHours) {
        // Session expired
        clearClientViewState();
        setSession(null);
      } else {
        setSession(stored);
      }
    }
  }, []);

  // Update session duration every second
  useEffect(() => {
    if (!session) {
      setSessionDuration(0);
      return;
    }

    const updateDuration = () => {
      const startTime = new Date(session.started_at).getTime();
      const now = Date.now();
      setSessionDuration(Math.floor((now - startTime) / 1000));
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [session]);

  // Exit client view and return to admin
  const exitClientView = useCallback(() => {
    if (!session) return;
    
    // Store the client ID before clearing for navigation
    const clientId = session.client_id;
    
    clearClientViewState();
    setSession(null);
    
    // Navigate back to the client detail page
    navigate(`/admin/clients/${clientId}`);
  }, [session, navigate]);

  const value: ClientViewContextType = useMemo(() => ({
    session,
    isViewingAsAdmin: !!session,
    effectiveClientId: session?.client_id || null,
    viewedClientName: session?.client_name || null,
    exitClientView,
    sessionDuration,
  }), [session, exitClientView, sessionDuration]);

  return (
    <ClientViewContext.Provider value={value}>
      {children}
    </ClientViewContext.Provider>
  );
};

export function useClientView(): ClientViewContextType {
  const context = useContext(ClientViewContext);
  if (!context) {
    throw new Error('useClientView must be used within a ClientViewProvider');
  }
  return context;
}

/**
 * Hook to check if mutations should be disabled
 * Returns true when in read-only client view mode
 */
export function useIsReadOnlyMode(): boolean {
  const context = useContext(ClientViewContext);
  // If no context (outside provider), allow mutations
  if (!context) return false;
  // Read-only when viewing as admin
  return context.isViewingAsAdmin;
}

export default ClientViewProvider;
